import { useTranslation } from 'react-i18next';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { PowerProfileDataPoint } from 'greener-ui-library';
import { Locale, localizedNumber } from 'utils/localization';

interface PowerProfileStatsDisplayProps {
  sx?: SxProps<Theme>;
  gap?: number;
  profileData?: PowerProfileDataPoint[];
  locale?: Locale;
  peakPower?: number | null;
  averagePower?: number | null;
  energyRequiredWeekly?: number | null;
  projectDurationWeeks?: number | null;
}

export const PowerProfileStatsDisplay = ({
  sx,
  gap = 4,
  peakPower = null,
  averagePower = null,
  energyRequiredWeekly = null,
  projectDurationWeeks = null,
  locale = 'en-GB',
}: PowerProfileStatsDisplayProps) => {
  const { t } = useTranslation();

  const energyRequiredProjectDuration =
    projectDurationWeeks && energyRequiredWeekly ? energyRequiredWeekly * projectDurationWeeks : null;

  return (
    <Box sx={{ display: 'flex', gap, mb: 3, ...sx }}>
      <Box>
        <Typography variant="caption" color="grey.800">
          {t('blueprints:wizard.fields.peakPower')}
        </Typography>
        <Typography data-testid="peak-power-value" variant="body1">
          {localizedNumber({ value: peakPower, unitLabel: 'kW', fractionDigits: 1, locale })}
        </Typography>
      </Box>

      <Box>
        <Typography variant="caption" color="grey.800">
          {t('blueprints:wizard.fields.averagePower')}
        </Typography>
        <Typography data-testid="average-power-value" variant="body1">
          {localizedNumber({ value: averagePower, unitLabel: 'kW', fractionDigits: 1, locale })}
        </Typography>
      </Box>

      <Box>
        <Typography variant="caption" color="grey.800">
          {t('blueprints:wizard.fields.energyRequiredWeekly')}
        </Typography>
        <Typography data-testid="energy-required-weekly" variant="body1">
          {localizedNumber({
            value: energyRequiredWeekly,
            unitLabel: 'kWh',
            fractionDigits: 1,
            locale,
          })}
        </Typography>
      </Box>

      {projectDurationWeeks ? (
        <Box>
          <Typography variant="caption" color="grey.800">
            {t('blueprints:wizard.fields.energyRequiredProjectDuration')}
          </Typography>
          <Typography data-testid="energy-required-project-duration" variant="body1">
            {localizedNumber({
              value: energyRequiredProjectDuration,
              unitLabel: 'kWh',
              fractionDigits: 1,
              locale,
            })}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};
